import Breadcrumbs from '@/components/Breadcrumbs';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import { type Course } from '@/components/CourseCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { BookOpen } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Group {
    id: number;
    title: string;
    image_url: string | null;
}

interface Props {
    group: Group;
    courses: Course[];
}

const GroupDetailContent: React.FC<Props> = ({ group, courses }) => {
    const { t } = useTranslation();

    return (
        <AppLayout>
            <PageTitle title={group.title} />
            <div className="mx-auto max-w-6xl space-y-6">
                <Breadcrumbs
                    items={[
                        { label: t('home.courses'), href: '/courses/' },
                        { label: group.title },
                    ]}
                />

                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                    {group.title}
                </h1>

                <div className="space-y-4">
                    {courses.map((course) => (
                        <div
                            key={course.id}
                            className="flex flex-col overflow-hidden rounded-lg bg-white shadow-md md:flex-row"
                        >
                            {/* Course Image */}
                            <div className="h-48 w-full flex-shrink-0 bg-gray-200 md:h-auto md:w-2/5">
                                {course.image ? (
                                    <img
                                        src={course.image}
                                        alt={course.title}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full min-h-[12rem] items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
                                        <BookOpen className="h-16 w-16 text-white opacity-50" />
                                    </div>
                                )}
                            </div>

                            {/* Course Content */}
                            <div className="flex flex-1 flex-col justify-center p-6">
                                <h3 className="mb-2 text-xl font-bold text-gray-900">
                                    {course.title}
                                </h3>
                                {course.description && (
                                    <div
                                        className="mb-4 line-clamp-3 text-sm text-gray-600"
                                        dangerouslySetInnerHTML={{
                                            __html: course.description,
                                        }}
                                    />
                                )}
                                <div>
                                    <PrimaryButton
                                        as="link"
                                        href={`/courses/${course.slug}/`}
                                        size="sm"
                                    >
                                        Accede aquí &rarr;
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </AppLayout>
    );
};

const GroupDetail: React.FC<Props> = (props) => {
    return (
        <ThemeWrapper>
            <GroupDetailContent {...props} />
        </ThemeWrapper>
    );
};

export default GroupDetail;
